import { useLiveQuery } from 'dexie-react-hooks'
import type { PayloadOf } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { mergeOverlay } from '../sync/merge.ts'

/** M16-I1: the photos of a procedure version (the mowing pattern, where the rakes go) are ordinary photos linked to the version with the purpose
 *  'illustration' (`core.document_version` admitted by Z's opening migration, rule 10 of docs/M16.md). A version is immutable, so the next one carries
 *  its own set: the publish form offers the pictures of the version in force (kept by default, re-linked through `attachment.link@1`: the bytes are
 *  stored once, under their hash) and takes new ones (`attachment.register@1`). Pure: the order, the limits and the payloads; nothing reads the network. */
export const ILLUSTRATION_PURPOSE = 'illustration'
export const ILLUSTRATION_ENTITY = 'core.document_version' as const
export const MAX_ILLUSTRATIONS = 6
/** the schema's bound (code points, as zod and the database count) */
export const MAX_CAPTION = 200

/** A row of the device's `core.attachment_link` mirror (a pending one from the projector carries the same fields). */
export interface IllustrationLink {
  id: string; org_id?: string; site_id: string; file_id: string; sha256: string; bytes: number; mime: string; entity_type: string; entity_id: string; purpose: string
  caption: string | null; captured_at: string | null; hidden_at: string | null
}

/** The device's view of every photo link (the server's rows with this device's pending registrations laid over), read live; the screens pick a version's with
 *  `illustrationsOf`. A club whose store is not open (a test's stand-in) reads none rather than failing. */
export function useIllustrationLinks(): IllustrationLink[] | undefined {
  const { db } = useClub()
  return useLiveQuery(async () => (db ? mergeOverlay((await db.attachment_link.toArray()) as unknown as IllustrationLink[], await db.overlay.where('table').equals('core.attachment_link').toArray()) : []), [db])
}

/** The illustrations of one version in the order they were added (the capture instant, then the id), a hidden one left out. */
export function illustrationsOf<T extends IllustrationLink>(links: readonly T[], versionId: string): T[] {
  return links
    .filter((l) => l.entity_id === versionId && l.entity_type === ILLUSTRATION_ENTITY && l.purpose === ILLUSTRATION_PURPOSE && !l.hidden_at)
    .sort((a, b) => (a.captured_at ?? '').localeCompare(b.captured_at ?? '') || a.id.localeCompare(b.id))
}

/** A picture of the version in force, offered on the next version's form: kept unless unticked; the caption may be reworded. */
export interface KeptIllustration { link: IllustrationLink; keep: boolean; caption: string }
export const keptOf = (links: readonly IllustrationLink[], versionId: string): KeptIllustration[] =>
  illustrationsOf(links, versionId).map((link) => ({ link, keep: true, caption: link.caption ?? '' }))

export const ILLUSTRATION_ERRORS = ['ILLUSTRATIONS_TOO_MANY', 'CAPTION_TOO_LONG'] as const
export type IllustrationError = (typeof ILLUSTRATION_ERRORS)[number]

export const captionLength = (s: string): number => [...s.trim()].length
/** A caption as the command takes it: the trimmed text, or nothing (the server stores an empty one as null anyway). */
export const captionValue = (s: string | undefined): string | undefined => { const c = (s ?? '').trim(); return c ? c : undefined }

export const illustrationCount = (kept: readonly KeptIllustration[], fresh: number): number => kept.filter((k) => k.keep).length + fresh

/** Why the version cannot be published yet, as far as its pictures go (shown as `planSetup.err.<CODE>`; the server checks the caption again). */
export function illustrationProblems(kept: readonly KeptIllustration[], fresh: readonly { caption: string }[]): IllustrationError[] {
  const out: IllustrationError[] = []
  if (illustrationCount(kept, fresh.length) > MAX_ILLUSTRATIONS) out.push('ILLUSTRATIONS_TOO_MANY')
  if ([...kept.filter((k) => k.keep), ...fresh].some((x) => captionLength(x.caption) > MAX_CAPTION)) out.push('CAPTION_TOO_LONG')
  return out
}

/** The links of the kept pictures on the new version (one `attachment.link@1` each; the file is the one already stored). */
export function buildIllustrationLinks(versionId: string, kept: readonly KeptIllustration[]): Array<PayloadOf<'attachment.link@1'>> {
  return kept.filter((k) => k.keep).map((k) => {
    const caption = captionValue(k.caption)
    return { file_id: k.link.file_id, entity_type: ILLUSTRATION_ENTITY, entity_id: versionId, purpose: ILLUSTRATION_PURPOSE, ...(caption ? { caption } : {}) }
  })
}
